"use strict";

// Imports
import { getQueryArgs } from "@js/nwcc-navbar.js";
import { renderGrowl } from "@js/nwcc-growl.js";
import { loadMainNwcc, setArgDetailsData } from "@js/nwcc-template.js";
import { areSetsEqual } from "@js/nwcc-utils.js";
import { createChart } from "../plotting/forecast-plots-plotting.js";
import { createEntries } from "../model/process-data.js";
import { getUrlArgs } from "./forecast-plots-url-args.js";
import * as getData from "../model/get-data.js";

// Constants, lists, objects, and globals
let ALLOW_URL_CHANGE = true; // Set to 'true' to allow changes to the GUI/Forms to change the URL search args.
const BASINS = window.basins; // window.basins located at "@js/basins.js"
// prettier-ignore
const ALL_PERIODS = ['APR-JUL', 'APR-SEP', 'MAY-JUL', 'MAY-SEP', 'JUN-JUL', 'JUN-SEP', 'APR', 'APR-AUG', 'APR-JUN', 'APR-MAY', 'APR-OCT', 'FEB', 'FEB-AUG', 'FEB-JUL', 'FEB-JUN', 'FEB-MAY', 'FEB-SEP', 'JAN', 'JAN-JUL', 'JAN-JUN', 'JAN-MAY', 'JAN-SEP', 'JUL', 'JUL-AUG', 'JUL-SEP', 'JUN', 'JUN-AUG', 'JUN-OCT', 'MAR', 'MAR-AUG', 'MAR-JUL', 'MAR-JUN', 'MAR-MAY', 'MAR-OCT', 'MAR-SEP', 'MAY', 'MAY-AUG', 'MAY-JUN', 'MAY-OCT', 'OCT-SEP', 'J16-JAN', 'J16-MAY', 'J16-JUN', 'F16-FEB', 'F16-MAY', 'F16-JUN', 'M16-MAR', 'M16-APR', 'M16-MAY', 'M16-JUN', 'A16-APR', 'A16-MAY', 'A16-JUN'];

/**
 * Runs on the DOMContentLoaded event. Does the following:
 *  - sets the application title in the page header (under the nav bar)
 *  - populates the years and pub dates menu based on the current date
 *  - populates the basins menu using the default state selection
 *  - reads any arguments passed to the url and updates the form
 *  - makes the chart using the selected options
 *  - adds event listeners to all the SELECTs/INPUTs/RADIOs
 *  - Sets up the arg-details component
 *  - removes the loading bar splash screen
 *  - stores the current page/chart state to the URL.
 */
export async function windowLoad() {
    setHeaderLongName();
    populateYearMenuAndPubDate();
    populateBasinsMenu();
    loadValuesFromURL();
    await makeChart();
    addEventListeners();
    setValuesToURL();
}
window.onload = async () => {
    await windowLoad();
    setArgDetailsData(getUrlArgs());
    loadMainNwcc();
};

/**
 * Sets the application header to include the necessary
 * forecast status.
 */
export function setHeaderLongName() {
    const forecastStausLongNames = {
        final: "",
        prelim: "Preliminary ",
        raw: "Raw ",
        guidance: "Guidance ",
    };
    const status = getForecastStatus();
    const statusLongName = forecastStausLongNames[status];
    if (status != "final") {
        const msg =
            "This is not final/approved forecast data, for internal use only! Please be advised this data should only be used for review prior to publishing.";
        renderGrowl(`${statusLongName}Forecast Data!`, msg, "warning", true, 5);
    }
    const longName = `${statusLongName}NWCC Seasonal Volume Forecast Plots`;
    document.querySelector("abbr[slot=application-long-name]").innerHTML = longName;
}

/**
 * Reads the search args string and returns the forecast status
 * if it is specified. otherwise, returns 'final'
 * @returns forecast status.
 */
export function getForecastStatus() {
    const args = getQueryArgs();
    return args["forecastStatus"] || "final";
}

/**
 * Uses the current date to populate the
 * year and pub-date drop down menus with options, as
 * well as setting the correct value;
 */
export function populateYearMenuAndPubDate() {
    // Populate pub date
    const today = new Date();
    let useMonth = today.getMonth() + 1;
    if (useMonth > 6) {
        useMonth = 6;
    }
    document.getElementById("pubDate").value = `${useMonth}-1`;

    // Populate Year Menu
    const menu = document.getElementById("year");
    while (menu.firstChild) {
        menu.removeChild(menu.firstChild);
    }
    const currentYear = today.getFullYear();
    for (let i = currentYear; i >= 1900; i--) {
        const option = document.createElement("option");
        option.value = i;
        option.innerHTML = i;
        menu.appendChild(option);
    }
    menu.value = currentYear;
}

/**
 * Uses the state selection drop-down along with the
 * Basins data file to populate the list of
 * basins for the selected state.
 */
export function populateBasinsMenu() {
    const menu = document.getElementById("basin");
    const selectedState = document.getElementById("state").value;
    while (menu.firstChild) {
        menu.removeChild(menu.firstChild);
    }

    // Custom basin
    if (Object.keys(getQueryArgs()).includes("customBasin")) {
        const customBasinString = getQueryArgs()["customBasin"];
        const op = document.createElement("option");
        op.innerHTML = "CUSTOM BASIN";
        op.value = "customBasin::" + customBasinString;
        menu.appendChild(op);
    }

    if (BASINS[selectedState].length) {
        for (const basin of BASINS[selectedState]) {
            const option = document.createElement("option");
            option.value = basin[0];
            option.innerHTML = basin[0];
            menu.appendChild(option);
        }
    }

    menu.value = menu.options[0].value;
}

/**
 * Reads the URL query arguments and loads each provided
 * argument into it's associated DOM input.
 */
export function loadValuesFromURL() {
    // Don't allow UI changes to change search args
    ALLOW_URL_CHANGE = false;

    // Don't allow hashes anymore (e.g. replace hash syntax with question mark syntax)
    if (window.location.href.includes("#")) {
        const url = new URL(window.location);
        url.search = url.hash.slice(1);
        url.hash = "";
        history.pushState(null, "", url.toString());
    }

    // Load search args
    const args = getQueryArgs();
    Object.keys(args).forEach((key) => {
        let value = args[key];
        if (typeof value === "string") {
            value = args[key].toUpperCase();
        }

        // period selection
        if (key === "period") {
            if (value === "ALL") {
                document.getElementById("period").value = "all";
            } else {
                const periods = value.split(",");
                for (const option of document.getElementById("period").options) {
                    option.selected = periods.includes(option.value);
                }
            }
            return;
        }

        // Check for drop down key
        const selects = document.querySelectorAll(`select[id=${key}]`);
        if (selects.length) {
            selects[0].value = value.replaceAll(/%20/g, " ");
            if (key === "state") {
                populateBasinsMenu();
            }
        }

        // Check for check-box key
        const checkboxes = document.querySelectorAll(`input[type=checkbox][id=${key}]`);
        if (checkboxes.length) {
            checkboxes[0].checked = value;
        }

        // Check for radio selections
        const radios = document.querySelectorAll(`input[type=radio][name=${key}]`);
        if (radios.length) {
            radios.forEach((radio) => {
                radio.checked = radio.value === value;
            });
        }
    });

    // RE-ALLOW url change on UI updates
    ALLOW_URL_CHANGE = true;
}

/**
 *
 * Reads the form values and stores the current values to
 * the URL searchParams, maintaining any non-form args
 * that were already in there (e.g. forecastStatus)
 */
export function setValuesToURL() {
    if (!ALLOW_URL_CHANGE) {
        return;
    }

    // get the inputs
    const inputs = getInputs();

    // Update the URL query
    const url = new URL(window.location);
    Object.entries(inputs).forEach(([key, value]) => {
        if (key === "period") {
            if (areSetsEqual(new Set(value), new Set(ALL_PERIODS))) {
                value = "all";
            }
        }
        if (key === "basin" && value.includes("customBasin::") && Object.keys(getQueryArgs()).includes("customBasin")) {
            url.searchParams.delete("basin");
            return;
        }
        url.searchParams.set(key, value.toString());
    });
    history.pushState(null, "", url.toString());
}

/**
 * Adds a function to all the SELECTs/INPUTs/RADIOs, such that
 * when their value changes the chart is re-drawn. Additionally,
 * adds a function to the State select drop down to re-populate the
 * basins.
 */
export function addEventListeners() {
    // get all the input nodes
    const inputNodes = Array.from(document.querySelectorAll("select, input[type=radio], input[type=checkbox]"));
    inputNodes.forEach((node) => {
        const f = () => {
            makeChart();
            setValuesToURL();
        };

        if (node.id === "state") {
            node.onchange = () => {
                populateBasinsMenu();
                f();
            };
        } else {
            node.onchange = f;
        }
    });
}

/**
 * Reads all the selects, and inputs and creates a object
 * containing all the inputted selections.
 *
 * The node id is the object key, and the selection
 * is the value.
 */
export function getInputs() {
    // get all the input nodes
    const inputNodes = Array.from(document.querySelectorAll("select, input[type=radio], input[type=checkbox]"));

    // Review each node and add an entry to the output object
    // depending on the type of node
    const inputs = Object.fromEntries(
        inputNodes
            .map((node) => {
                if (node.tagName === "SELECT") {
                    if (node.id === "period") {
                        if (node.value === "all") {
                            return [node.id, ALL_PERIODS];
                        }
                        const periodList = [];
                        for (const option of node.options) {
                            if (option.selected) {
                                periodList.push(option.value);
                            }
                        }
                        return [node.id, periodList];
                    } else {
                        return [node.id, node.value];
                    }
                } else if (node.type === "radio") {
                    return node.checked ? [node.name, node.value] : [null, null];
                } else {
                    return [node.name, node.checked];
                }
            })
            .filter((n) => n[0] !== null),
    );

    return inputs;
}

/**
 * This function:
 *  - Gathers the selected options into the "inputs" object
 *  - Gets the list of forecast points for the selected stations
 *  - Gets the observations as well as the historical stats
 *  - Gets the forecasts (either from JSON factory or from REST depending on forecast status)
 *  - Creates individual data entries (1 entry per row in the chart.)
 *  - draws the forecast chart.
 */
export async function makeChart() {
    // Read inputs
    const inputs = getInputs();

    // Derive some other inputs
    inputs.pubDateText = document.getElementById("pubDate").selectedOptions[0].text;
    inputs.pubDateText += ", " + inputs.year;

    // If there's no inputs, hide the SVG
    if (!inputs.state || !inputs.basin || !inputs.pubDate || !inputs.period.length) {
        document.querySelectorAll("#chart > *").forEach((e) => e.remove());
        document.getElementById("chart").style.background = "transparent";
    }

    // Get ID's for selected basins
    const stations = getStationsForBasin(inputs.state, inputs.basin);
    if (inputs.basin.includes("customBasin::")) {
        inputs.basin = inputs.basin.split(",")[0];
        inputs.basin = inputs.basin.slice(13);
    }

    // Get historical oberserved data/stats
    let obsPromise = null;
    if (inputs.showObserved || inputs.showMax || inputs.showMin || inputs.showNumberObservations || !inputs.hideEmpty) {
        obsPromise = getData.getJSONFactoryForecastFile("SRVO", "MONTHLY", "PERIOD", inputs.pubDate);
    }
    const statsPromise = getData.getJSONFactoryForecastFile("SRVO", "MONTHLY", `PERIOD${inputs.normalType}`, "ALL");

    // Get Forecasts
    const forecastStatus = getForecastStatus();
    inputs.forecastStatus = forecastStatus;
    let forecastPromise = null;
    if (forecastStatus !== "final") {
        const [pubMonth, pubDay] = inputs.pubDate.split("-");
        forecastPromise = getData.getRESTForecastFile(inputs.year, pubMonth, pubDay, stations, forecastStatus);
    } else {
        forecastPromise = getData.getJSONFactoryForecastFile(
            "SRVO",
            "MONTHLY",
            "FORECAST",
            `${inputs.year}-${inputs.pubDate}`,
        );
    }

    // After all promises finish, draw the SVG
    const promiseValues = await Promise.all([obsPromise, statsPromise, forecastPromise]);
    const [obsData, statsData, forecastsData] = promiseValues;
    const entries = createEntries(obsData, statsData, forecastsData, stations, inputs);

    createChart(entries, inputs);
}

/**
 *
 * @param {*} state
 * @param {*} basin
 * @returns a list of forecast stations in the given basin and state
 */
export function getStationsForBasin(state, basin) {
    if (basin.includes("customBasin::")) {
        return basin.split(",").slice(1);
    }
    for (const basinList of BASINS[state]) {
        if (basinList[0] === basin) {
            return basinList.slice(1);
        }
    }
    return [];
}

/**
 * Reads the contents of the 'chart' element (all the contents
 * are raw SVG), and creates an anchor element with the svg
 * contents in the 'download' attribute. The anchor is clicked
 * and then removed.
 */
export function exportSVG() {
    const svg = document.getElementById("chart");
    const svgText = svg.outerHTML;
    const basin = document.getElementById("basin").value.replaceAll(" ", "-");
    const pubDate = document.getElementById("pubDate").selectedOptions[0].innerHTML;
    const year = document.getElementById("year").selectedOptions[0].innerHTML;
    const filename = `${basin}_${pubDate.replaceAll(" ", "_")}_${year}.svg`;
    const d = "data:text/plain;charset=utf-8," + encodeURIComponent(svgText);
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", d);
    downloadAnchorNode.setAttribute("download", filename);
    document.body.appendChild(downloadAnchorNode); // required for firefox
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
}
document.getElementById("export--svg").onclick = exportSVG;

/**
 * Reads the contents of the 'chart' element (all the contents
 * are raw SVG). The contents are converted to PNG via a canvas
 * element intermediary. creates an anchor element with the PNG
 * contents in the 'download' attribute. The anchor is clicked
 * and then removed.
 */
export function exportPNG() {
    const svg = document.getElementById("chart");
    const svgText = svg.outerHTML;
    const canvas = document.createElement("canvas");
    canvas.width = +svg.getAttribute("width");
    canvas.height = +svg.getAttribute("height");
    const imageObj = document.createElement("img");
    imageObj.width = canvas.width;
    imageObj.height = canvas.height;
    imageObj.src = "data:image/svg+xml;utf8," + encodeURIComponent(svgText);
    imageObj.onload = () => {
        const context = canvas.getContext("2d");
        context.drawImage(imageObj, 0, 0);
        const d = canvas.toDataURL();
        const downloadAnchorNode = document.createElement("a");
        downloadAnchorNode.setAttribute("href", d);
        const basin = document.getElementById("basin").value.replaceAll(" ", "-");
        const pubDate = document.getElementById("pubDate").selectedOptions[0].innerHTML;
        const year = document.getElementById("year").selectedOptions[0].innerHTML;
        const filename = `${basin}_${pubDate.replaceAll(" ", "_")}_${year}.png`;
        downloadAnchorNode.setAttribute("download", filename);
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };
}
document.getElementById("export--png").onclick = exportPNG;
